export const callAI = async (prompt, systemPrompt, settings) => {
  const { apiProvider, zaiApiKey, openRouterApiKey, model } = settings;

  let endpoint = '';
  let apiKey = '';
  let selectedModel = model;

  if (apiProvider === 'zai') {
    endpoint = 'https://api.z.ai/api/coding/paas/v4/chat/completions';
    apiKey = zaiApiKey;
    if (!selectedModel) selectedModel = 'glm-4.7-flash';
  } else if (apiProvider === 'openrouter') {
    endpoint = 'https://openrouter.ai/api/v1/chat/completions';
    apiKey = openRouterApiKey;
    if (!selectedModel) selectedModel = 'openai/gpt-4o-mini';
  }

  if (!apiKey) {
    throw new Error('APIキーが設定されていません。設定画面からAPIキーを入力してください。');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: selectedModel,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ],
      response_format: { type: 'json_object' }
    })
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'AI API Error');
  }

  const data = await response.json();
  const text = data.choices[0].message.content;

  const extractJSON = (str) => {
    const match = str.match(/```(?:json)?\s*([\s\S]+?)\s*```/);
    if (match) return JSON.parse(match[1]);
    return JSON.parse(str);
  };

  return extractJSON(text);
};

// Collect all cells that are currently empty (no content)
const getEmptyCellIds = (chartData) => {
  const emptyIds = new Set();
  chartData.blocks.forEach(block => {
    block.cells.forEach(cell => {
      if (!cell.content || cell.content.trim() === '') {
        emptyIds.add(cell.id);
      }
    });
  });
  return emptyIds;
};

// Sync linked cells: CORE peripheral ↔ BLOCK center
const syncLinkedCells = (chart, emptyIds) => {
  const coreBlock = chart.blocks.find(b => b.block_type === 'CORE');
  if (!coreBlock) return;

  for (let i = 1; i <= 8; i++) {
    const coreCell = coreBlock.cells.find(c => !c.is_center && c.priority_index === i);
    const targetBlock = chart.blocks.find(b => b.block_type === `BLOCK_${i}`);
    const blockCenter = targetBlock?.cells.find(c => c.is_center);

    if (!coreCell || !blockCenter) continue;

    const coreWasEmpty = emptyIds.has(coreCell.id);
    const blockWasEmpty = emptyIds.has(blockCenter.id);

    // If one was pre-existing and the other was AI-filled, use pre-existing content
    if (!coreWasEmpty && blockWasEmpty) {
      blockCenter.content = coreCell.content;
    } else if (coreWasEmpty && !blockWasEmpty) {
      coreCell.content = blockCenter.content;
    }
    // If both were AI-filled, they should already match (AI generates them consistently)
    // If both were pre-existing, don't change either
  }
};

export const generateFullChartByAI = async (chartData, settings) => {
  // Check for empty cells first — skip AI if everything is filled
  const emptyIds = getEmptyCellIds(chartData);
  if (emptyIds.size === 0) {
    return null; // Signal: no empty cells
  }

  const coreBlock = chartData.blocks.find(b => b.block_type === 'CORE');
  const centerTheme = coreBlock?.cells.find(c => c.is_center)?.content || '目標達成';

  // Build a list of which positions are already filled so AI can focus on empties
  const filledInfo = {};
  chartData.blocks.forEach(block => {
    let blockKey = block.block_type === 'CORE' ? 'core_block' : block.block_type.toLowerCase();
    const filledPositions = [];
    block.cells.forEach(cell => {
      if (!cell.is_center && cell.content && cell.content.trim() !== '') {
        filledPositions.push({ priority: cell.priority_index, content: cell.content });
      }
    });
    if (filledPositions.length > 0) {
      filledInfo[blockKey] = filledPositions;
    }
  });

  const prompt = `メインテーマ: ${centerTheme}
既に埋まっているセル: ${JSON.stringify(filledInfo)}`;

  const systemPrompt = `あなたは優秀な思考整理のアシスタントです。提供されたメインテーマに基づき、9×9のチャートの要素（計81マス分）を厳密なJSON形式で返却してください。

重要: 「既に埋まっているセル」に記載された位置の内容は、既存の内容を尊重し、必ず同じ内容を出力してください。空きマスのみに新しい内容を提案してください。

メインテーマを意識して、各サブテーマの周辺マスにはメインテーマに関連した具体的・行動可能な言葉を選んでください。例えば「ダイエット」がメインテーマで「日々のルーチン」がサブテーマなら、一般的なルーチンではなく「ダイエットに関連するルーチン」を提案してください。

重複を避け、具体的で行動可能な短い言葉（15文字以内）を生成してください。出力形式は以下の構造のJSONのみとしてください：
{
  "core_block": { "center": "メインテーマ", "1": "サブテーマ1", "2": "サブテーマ2", "3": "サブテーマ3", "4": "サブテーマ4", "5": "サブテーマ5", "6": "サブテーマ6", "7": "サブテーマ7", "8": "サブテーマ8" },
  "block_1": { "center": "サブテーマ1", "1": "要素1-1", "2": "要素1-2", "3": "要素1-3", "4": "要素1-4", "5": "要素1-5", "6": "要素1-6", "7": "要素1-7", "8": "要素1-8" },
  "block_2": { "center": "サブテーマ2", "1": "要素2-1", "2": "要素2-2", "3": "要素2-3", "4": "要素2-4", "5": "要素2-5", "6": "要素2-6", "7": "要素2-7", "8": "要素2-8" },
  "block_3": { "center": "サブテーマ3", "1": "要素3-1", "2": "要素3-2", "3": "要素3-3", "4": "要素3-4", "5": "要素3-5", "6": "要素3-6", "7": "要素3-7", "8": "要素3-8" },
  "block_4": { "center": "サブテーマ4", "1": "要素4-1", "2": "要素4-2", "3": "要素4-3", "4": "要素4-4", "5": "要素4-5", "6": "要素4-6", "7": "要素4-7", "8": "要素4-8" },
  "block_5": { "center": "サブテーマ5", "1": "要素5-1", "2": "要素5-2", "3": "要素5-3", "4": "要素5-4", "5": "要素5-5", "6": "要素5-6", "7": "要素5-7", "8": "要素5-8" },
  "block_6": { "center": "サブテーマ6", "1": "要素6-1", "2": "要素6-2", "3": "要素6-3", "4": "要素6-4", "5": "要素6-5", "6": "要素6-6", "7": "要素6-7", "8": "要素6-8" },
  "block_7": { "center": "サブテーマ7", "1": "要素7-1", "2": "要素7-2", "3": "要素7-3", "4": "要素7-4", "5": "要素7-5", "6": "要素7-6", "7": "要素7-7", "8": "要素7-8" },
  "block_8": { "center": "サブテーマ8", "1": "要素8-1", "2": "要素8-2", "3": "要素8-3", "4": "要素8-4", "5": "要素8-5", "6": "要素8-6", "7": "要素8-7", "8": "要素8-8" }
}`;

  const aiData = await callAI(prompt, systemPrompt, settings);

  const newChart = JSON.parse(JSON.stringify(chartData));

  // Only fill empty cells — never overwrite existing content
  newChart.blocks.forEach(block => {
    let blockKey = block.block_type === 'CORE' ? 'core_block' : block.block_type.toLowerCase();
    const aiBlockData = aiData[blockKey];
    if (aiBlockData) {
      block.cells.forEach(cell => {
        if (cell.is_center) {
          // Only fill center if it was empty
          if (!cell.content || cell.content.trim() === '') {
            cell.content = aiBlockData.center || cell.content;
          }
        } else {
          // Only fill peripheral if it was empty
          if (!cell.content || cell.content.trim() === '') {
            cell.content = aiBlockData[cell.priority_index] || cell.content;
          }
        }
      });
    }
  });

  // Sync linked cells: ensure CORE peripheral ↔ BLOCK center consistency
  syncLinkedCells(newChart, emptyIds);

  return newChart;
};

export const generateAssistCellsByAI = async (chartData, activeBlockId, settings) => {
  const activeBlock = chartData.blocks.find(b => b.id === activeBlockId);
  const centerCell = activeBlock.cells.find(c => c.is_center);

  const coreBlock = chartData.blocks.find(b => b.block_type === 'CORE');
  const mainTheme = coreBlock?.cells.find(c => c.is_center)?.content || '未設定';

  const emptyCells = activeBlock.cells.filter(c => !c.is_center && (!c.content || c.content.trim() === ''));
  const missingPriorities = emptyCells.map(c => c.priority_index);

  if (missingPriorities.length === 0) return [];

  const existingWords = [];
  chartData.blocks.forEach(b => {
    b.cells.forEach(c => {
      if (c.content && c.content.trim() !== '') existingWords.push(c.content);
    });
  });

  const prompt = `全体のメインテーマ（目標）: ${mainTheme}
現在のブロックテーマ（サブテーマ）: ${centerCell.content || '未設定'}
補完する優先順位（空きマス）: ${missingPriorities.join(', ')}
既存のワード（重複禁止）: ${existingWords.join(', ')}`;

  const systemPrompt = `あなたは優秀な思考整理のアシスタントです。
「全体のメインテーマ」を達成するために、「現在のブロックテーマ」についてより詳細なアクションや要素を提案してください。
例えば、メインテーマが「ダイエット」で、ブロックテーマが「日々のルーチン」の場合、一般的なルーチンではなく「ダイエットに関連する日々のルーチン（例：朝のストレッチ、15分のウォーキング、糖質制限など）」を出力してください。
既存のワードとは絶対に内容や表現が重複しない、ユニークで具体的・行動可能な短い言葉（15文字以内）にしてください。
指定された missing_priorities の数だけ、以下の形式の厳密なJSONで言葉を返却してください。
{
  "results": [
    { "priority": 1, "content": "新しいアイデア" }
  ]
}`;

  const aiData = await callAI(prompt, systemPrompt, settings);
  return aiData.results || [];
};